import { useCallback, startTransition } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useSearchParamPatch } from '../../hooks/useSearchParamPatch'
import { useLocaleContext } from 'fbtee'
import { daysToEventBucket, festivalPhase, formatEventDateRange } from '../../lib/formatDate'
import { useTrackView } from '../../hooks/useTrackView'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { usePageLoadTrace } from '../../lib/perf'
import { Skeleton } from '../../components/ui/skeleton'
import { PageLayout } from '../../components/PageLayout'
import { NotFoundState } from '../../components/NotFoundState'
import { ErrorState } from '../../components/ErrorState'
import { CreateGroupDialog } from '../../components/CreateGroupDialog'
import { EventShareButton } from '../../components/EventShareButton'
import { ScheduleShareFab } from './ScheduleView/ScheduleShareFab'
import { cn } from '../../lib/utils'
import { track } from '../../lib/analytics'
import { festivalWindow } from '@shared/dates'
import { eventDayCutoff } from '@shared/dayCutoff'
import { ScheduleView } from './ScheduleView'
import { LineupView } from './LineupView'
import { SocialView } from './SocialView'
import { CrewTopPicksSection } from './CrewTopPicksSection'
import { PartyHome } from './PartyHome'
import { FriendScopeBanner } from './FriendScopeBanner'
import { CancelledNotice } from './CancelledNotice'
import { useEventMilestonesSeen } from '../../hooks/useEventMilestonesSeen'
import { MIN_LINEUP_ARTISTS } from '@shared/emailMilestones'
import { InfoView } from './InfoView'
import { AftersView } from './AftersView'
import { useChildEvents } from '../../hooks/useChildEvents'
import { useEventPageData } from './useEventPageData'
import { useCrewUnread } from '../../hooks/useCrewUnread'
import { useFriendEventArtistInterests } from '../../hooks/useFriendEventArtistInterests'
import type { Set as FestivalSet } from '../../types'
import type { ViewTab } from './ViewPillNav'
import { ViewPillNavRow } from './ViewPillNavRow'
import { eventNotFoundMessage } from '../../lib/commonStrings'

const NO_IDS: string[] = []

const VALID_TABS: ViewTab[] = ['schedule', 'lineup', 'social', 'info', 'afters']

function resolveActiveTab(
  tabParam: string | null,
  event: { type?: string } | null,
  hasSchedule: boolean,
  hasLineup: boolean,
  lineupLoading: boolean,
  showAfters: boolean,
): ViewTab {
  // A stale ?tab=afters link to a festival with no afters falls through to the
  // default rather than landing on a tab whose pill isn't there.
  if (tabParam === 'afters' && !showAfters) tabParam = null
  if (tabParam && VALID_TABS.includes(tabParam as ViewTab)) return tabParam as ViewTab
  if (!event) return 'schedule'
  if (!hasSchedule && !lineupLoading) return hasLineup ? 'lineup' : 'info'
  return 'schedule'
}

export default function EventPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const { locale } = useLocaleContext()
  const [searchParams, patchSearchParams] = useSearchParamPatch()
  const tabParam = searchParams.get('tab') as ViewTab | null
  const selectedStage = searchParams.get('stage')
  // Transient "flash this set" signal (set by scheduleSetPath callers). The
  // schedule switches to the set's own day off this id alone; DayGrid scrolls to
  // + pulses the block, then we strip the param so reload/back doesn't re-fire.
  const highlightSetId = searchParams.get('set')
  const setView = useCallback((v: ViewTab) => {
    patchSearchParams({ tab: v })
  }, [patchSearchParams])

  const setSelectedStage = useCallback((stage: string) => {
    patchSearchParams({ stage })
  }, [patchSearchParams])

  const clearHighlightSet = useCallback(() => {
    patchSearchParams({ set: null })
  }, [patchSearchParams])

  // Schedule search picked a set: flash it. The schedule follows the set to its
  // own day, so the id is the whole signal.
  const goToScheduleSet = useCallback((set: FestivalSet) => {
    patchSearchParams({ tab: 'schedule', set: set.id })
    track('schedule_search_select', { event_id: eventId! })
  }, [patchSearchParams, eventId])

  const data = useEventPageData(eventId!)
  const {
    event, stages, artists, artistsLoading, lineup, allSets, setsLoading,
    lineupLoading, hasSchedule, days, defaultDay, loading, error, venue,
    eventStatus, eventStatusLoading, mergedUserProfiles, profilesLoading, currentUserId, signedInUid, viewInterestCount,
    friendScopeId, isGuest, groups, createGroup, linkedGroups, linkGroup, unlinkGroup, canUnlinkGroup, friendIds,
    interestedArtistIds, guardedToggleArtistInterest, guardedMarkArtists, otherMemberIds,
    followHintIds, followCtaArtistIds, addFollowedArtists,
    interestedSetIds, goingSetIds, cycleSetInterest, setUserIds,
    partialRangeBySetId, updateSetPartialRange, setRanges,
    goingUserIds, interestedUserIds, peopleLoading, totemMarking, setFeed,
    createGroupOpen, setCreateGroupOpen, handleCreateGroup,
    tipSetId, dismissSetTip, handleSetInterest,
  } = data

  const hasLineup = !lineupLoading && lineup.length > 0
  const isParty = event?.type === 'party'
  const afters = useChildEvents(event?.type === 'festival' ? event.id : null)
  const showAfters = afters.events.length > 0
  // A ?tab=afters deep link stays put while the list loads instead of bouncing
  // through the schedule; the pill itself waits for real rows.
  const activeTab = resolveActiveTab(
    tabParam, event, hasSchedule, hasLineup, lineupLoading, showAfters || afters.loading,
  )
  useDocumentTitle(event ? pageTitle(event.name) : null)
  // Only the lineup's facepiles (a party's home embeds it) and the Crew tab's
  // top picks read crew artist interests.
  const needsArtistInterests = isParty || activeTab === 'lineup' || activeTab === 'social'
  const { artistUserIds, loading: artistInterestsLoading } = useFriendEventArtistInterests(needsArtistInterests ? otherMemberIds : NO_IDS, eventId!)

  const crewUnread = useCrewUnread(eventId!, currentUserId, setFeed.allPosts, activeTab === 'social')
  useEventMilestonesSeen(signedInUid, event?.id, {
    lineup: !isParty && activeTab === 'lineup' && lineup.length >= MIN_LINEUP_ARTISTS,
    schedule: !isParty && activeTab === 'schedule' && hasSchedule,
    cancelled: event?.cancelled === true,
  })

  useTrackView(
    'event_viewed',
    event && {
      event_id: event.id,
      event_type: event.type,
      festival_phase: festivalPhase(event),
      days_to_event: daysToEventBucket(event),
      interest_count: viewInterestCount,
    },
  )

  usePageLoadTrace(
    'event_page_to_content',
    !loading && !setsLoading && !lineupLoading && !artistsLoading,
    { view: activeTab },
  )

  // Set days outside the official dates (a pre-party, a late closing night)
  // widen the window the festival-mode chip treats as live.
  const liveWindow = event?.type === 'festival' ? festivalWindow(event, days) : undefined

  if (error) return <ErrorState error={error} />

  if (!loading && !event) {
    return (
      <NotFoundState
        message={eventNotFoundMessage()}
      />
    )
  }

  // Links minted before the schedule became its own route.
  if (isParty && tabParam === 'schedule') {
    return <Navigate to={`/events/${eventId}/schedule`} replace />
  }

  const dateRange = event ? formatEventDateRange(event.startDate, event.endDate, locale) : ''
  const fillViewport = !isParty && activeTab === 'schedule'

  return (
    <PageLayout
      fillViewport={fillViewport}
      className={cn('max-w-5xl w-full mx-auto px-4 py-4', fillViewport && 'gap-3')}
      back={true}
      title={
        event ? (
          <h1 className="text-base font-semibold truncate">{event.name}</h1>
        ) : (
          <Skeleton className="h-5 w-32" />
        )
      }
      // Parties carry their own share controls over the hero image; festivals
      // have no hero, so the header is their only *event* share entry point.
      // Sharing your own schedule lives on the dedicated FAB in the grid.
      action={
        event && event.type === 'festival' ? (
          <EventShareButton eventId={event.id} eventName={event.name} />
        ) : undefined
      }
    >
      <CreateGroupDialog
        open={createGroupOpen}
        onOpenChange={setCreateGroupOpen}
        onCreate={createGroup}
        onCreated={(id, name) => linkGroup(id, 'event_page', name)}
      />

      {event?.cancelled && <CancelledNotice reason={event.cancelledReason} />}

      {friendScopeId && friendScopeId !== currentUserId && (
        <FriendScopeBanner profile={mergedUserProfiles[friendScopeId]} />
      )}

      {isParty && event ? (
        <PartyHome event={event} venue={venue} dateRange={dateRange} data={data} artistUserIds={artistUserIds} />
      ) : (
        <>
      <ViewPillNavRow
        view={activeTab}
        onViewChange={v => { startTransition(() => setView(v)); track('view_tab_switch', { tab: v }) }}
        crewUnread={crewUnread}
        showAfters={showAfters}
        interestStatus={eventStatus}
        interestLoading={eventStatusLoading}
        onSetStatus={handleSetInterest}
      />

      {activeTab === 'schedule' ? (
        <ScheduleView
          days={days}
          defaultDay={defaultDay}
          eventId={eventId!}
          onSelectSearchResult={goToScheduleSet}
          sets={allSets}
          setsLoading={setsLoading}
          stages={stages}
          artists={artists}
          hasSchedule={hasSchedule}
          interestedSetIds={interestedSetIds}
          goingSetIds={goingSetIds}
          onCycleSet={cycleSetInterest}
          setUserIds={setUserIds}
          setRanges={setRanges}
          ownRanges={partialRangeBySetId}
          onUpdateOwnRange={updateSetPartialRange}
          userProfiles={mergedUserProfiles}
          currentUserId={currentUserId}
          tipSetId={tipSetId}
          onDismissTip={dismissSetTip}
          feed={setFeed}
          totem={totemMarking}
          highlightSetId={highlightSetId}
          onHighlightConsumed={clearHighlightSet}
          timezone={venue?.timezone}
          dayCutoff={eventDayCutoff(event)}
          festivalMode={
            eventId && liveWindow
              ? { eventId, startDate: liveWindow.startDate, endDate: liveWindow.endDate }
              : undefined
          }
          surface="event_schedule"
          wallpaper={event ? { eventName: event.name } : undefined}
          shareFab={
            event ? (
              <ScheduleShareFab
                eventId={event.id}
                eventName={event.name}
                ownerId={signedInUid}
                groups={linkedGroups}
                setCount={interestedSetIds.size + goingSetIds.size}
                surface="event_schedule"
              />
            ) : undefined
          }
        />
      ) : activeTab === 'lineup' ? (
        <LineupView
          lineup={lineup}
          lineupLoading={lineupLoading}
          lineupMode={event?.lineupMode ?? 'day'}
          stages={stages}
          artists={artists}
          interestedArtistIds={interestedArtistIds}
          onToggleArtist={guardedToggleArtistInterest}
          onMarkArtists={guardedMarkArtists}
          followHintIds={followHintIds}
          followCtaArtistIds={followCtaArtistIds}
          onAddFollowed={addFollowedArtists}
          artistUserIds={artistUserIds}
          userProfiles={mergedUserProfiles}
          currentUserId={currentUserId}
          defaultDay={defaultDay}
          selectedStage={selectedStage}
          onSelectedStageChange={setSelectedStage}
          feed={setFeed}
        />
      ) : activeTab === 'social' ? (
        <SocialView
          groups={groups}
          linkedGroups={linkedGroups}
          onUnlinkGroup={unlinkGroup}
          canUnlinkGroup={canUnlinkGroup}
          isGuest={isGuest}
          onCreateGroup={handleCreateGroup}
          goingUserIds={goingUserIds}
          interestedUserIds={interestedUserIds}
          peopleLoading={peopleLoading}
          userProfiles={mergedUserProfiles}
          profilesLoading={profilesLoading}
          currentUserId={currentUserId}
          friendIds={friendIds}
          feed={setFeed}
          artists={artists}
          sets={allSets}
          hasSchedule={hasSchedule}
          onNavigateToSet={goToScheduleSet}
          eventId={eventId!}
          event={event}
          topPicks={
            <CrewTopPicksSection
              data={data}
              artistUserIds={artistUserIds}
              artistInterestsLoading={artistInterestsLoading}
              onNavigateToSet={goToScheduleSet}
            />
          }
        />
      ) : activeTab === 'afters' ? (
        <AftersView afters={afters.events} loading={afters.loading} error={afters.error} />
      ) : event ? (
        <InfoView event={event} dateRange={dateRange} venue={venue} />
      ) : (
        <div className="space-y-3">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}
        </>
      )}
    </PageLayout>
  )
}
